/* Eén woordenboek. Geen `lang === "ru" ? … : …` verspreid door de app.

   Russisch is de sleutelbron: `ru` bepaalt welke sleutels bestaan en welke
   vorm ze hebben. `en` wordt daar typematig aan opgehangen, dus een vergeten
   of verkeerd getypte sleutel is een compileerfout en geen lege plek op het
   scherm. Bewust géén `as const` op `ru`: anders zou `en` letterlijk dezelfde
   Russische tekst moeten bevatten.

   Waarden met een variabele erin zijn functies. Zo blijft de volgorde van de
   zin per taal vrij -- in het Russisch staat het getal soms achteraan.

   Wat hier NIET in staat: alles wat R en Н zelf schrijven. Brieven, antwoorden,
   woordenboekregels, radiotoelichtingen en foto-onderschriften blijven altijd
   in de taal waarin ze geschreven zijn. Vertalen zou hun stem vervalsen. */

/* Russische meervoudsvorm: 1 день · 2 дня · 5 дней (en 21 день, 22 дня…). */
export const ruPlural = (n: number, one: string, few: string, many: string): string => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const ru = {
  /* ── districten: eigennaam + wat het is ───────────────────────────────── */
  dSvyazi: "Площадь связи",
  dSvyaziSub: "где вы встречаетесь",
  dSignal: "Сигнальный квартал",
  dSignalSub: "то, что звучит",
  dArchive: "Архивный квартал",
  dArchiveSub: "то, что осталось",
  dVokzal: "Вокзальный квартал",
  dVokzalSub: "то, что впереди",

  /* ── statussen op de ingangen ─────────────────────────────────────────── */
  bothHere: "вы оба здесь",
  quiet: "тихо",
  questionOfDay: "вопрос дня",
  waitingSecondStar: "ждём вторую звезду",
  constellationDone: "созвездие собрано ✦",
  unread: (n: number) => `${n} непрочитано`,
  onTheWay: (pct: number) => `в пути · ${pct}%`,
  oneSkyOpen: "одно небо · открыть",
  oneSky: "одно небо",
  skyTitle: "Одно небо",
  skyPlace: "поставить звезду",
  skyNote: (seconds: number) => `Одно небо на двоих · ${seconds} сек. Почти одновременные касания становятся метеорами. Созвездие останется.`,
  skyRemaining: (n: number) => `твоих звёзд сегодня осталось: ${n}`,
  skyFull: "Твоя часть неба на сегодня готова. Созвездие осталось — завтра будет новое место.",
  skySendFailed: "Звезда не дошла. Проверь связь и попробуй ещё раз.",
  letterOnTheWay: "письмо в пути",
  youHomeSheNot: "партнёр сейчас не в приложении",
  daysBuilt: (n: number) => `${n} дней построено`,
  daysShort: (n: number) => `${n} дней`,
  emptyYet: "пока пусто",
  /* ── вертрекборд: до встречи ──────────────────────────────────────────── */
  cdDays: (n: number) => `до встречи ${n} ${ruPlural(n, "день", "дня", "дней")}`,
  cdHours: (n: number) => `до встречи ${n} ${ruPlural(n, "час", "часа", "часов")}`,
  cdTogether: "вы вместе",
  /* Machinestatussen zijn domeintaal; op het scherm staan gecureerde woorden. */
  chState: (s: string): string => ({ APART: "врозь", APPROACH: "навстречу",
    TOGETHER: "вместе", AFTERGLOW: "эхо встречи", ARCHIVE: "архив" } as
    Record<string, string>)[s] ?? s.toLowerCase(),
  mastDay: (n: number) => `день ${n}`,
  gcTitle: "Центральный сад",
  gcSub: "зелёное сердце города",
  msNewStreet: (ch: string) => `${ch} открыла новую улицу`,
  projectWing: "Дом проектов",
  projectsBuilt: (n: number) => `${n} ${n === 1 ? "проект построен" : "проектов построено"}`,
  railHint: "проведи вбок, чтобы увидеть больше →",
  posterRowAria: "городские афиши — дни с фотографиями",
  posterProv: (when: string) => `Построено из ${when} · ваш фотодень`,
  onAir: "эфир",
  cabinetOfDay: "кабинет дня",
  window: "витрина",
  weekProject: "проект недели",
  sealed: "запечатано",
  twoPunches: "два компостера",

  /* ── ingangen: eigennaam + ondertitel ─────────────────────────────────── */
  eToday: "Сегодня",
  eTodaySub: "обсерватория",
  ePost: "Почта",
  ePostSub: "ночное отделение",
  eWindows: "Окна",
  eWindowsSub: "онлайн вместе",
  eRadio: "Радио 2500",
  eRadioSub: "крыша",
  eArcade: "Аркада",
  eArcadeSub: "неоновый подвал",
  eAtelier: "Crincident",
  eAtelierSub: "ателье",
  eMuseum: "Архив",
  eMuseumSub: "прошедшие дни",
  eKiosk: "Киоск",
  eKioskSub: "газета недели",
  eTower: "Часовая башня",
  eTowerSub: "капсулы",
  eStation: "Станция",
  eTickets: "Билеты",
  eTicketsSub: "планер и Today Pass",
  eNew: "NEW",
  eNewSub: "другая сторона города",
  eNewMeta: "открыть новый вид",

  /* ── Сад между нами: zichtbare, blijvende groei ─────────────────────── */
  gKicker: "растёт вместе с вами",
  gTitle: "Сад между нами",
  gSubtitle: "каждый день оставляет что-то живое",
  gOpen: "войти в сад",
  gStages: ["семя", "первый росток", "молодой сад", "сад света",
            "в полном цвету", "вечный сад"],
  gStageNow: (stage: string) => `сейчас · ${stage}`,
  gNext: (n: number) => `ещё ${n} очков роста до следующей формы`,
  gGrowthHelp: "День вместе добавляет 4 очка роста, доставленное письмо или запечатанная капсула — 3, открытая капсула, песня или общая искра — 2. Новая глава добавляет 12. Сад растёт в вашем темпе.",
  gGrowthTitle: "Как растёт сад",
  gComplete: "сад продолжает расти без последней ступени",
  gBenchTitle: "Скамейка воспоминаний",
  gBenchSub: (n: number) => `${n} общих дней ждут здесь`,
  gBenchOpen: "перенести нас куда-нибудь",
  gDays: "дни",
  gLetters: "фонари-письма",
  gCapsules: "капсулы",
  gSongs: "светлячки-песни",
  gSparks: "общие искры",
  gLegendDay: "дни становятся листьями",
  gLegendLetter: "письма зажигают фонари",
  gLegendCapsule: "капсулы становятся бутонами и цветами",
  gLegendSong: "песни выпускают светлячков",
  gLegendSpark: "совпадения зажигают искры",
  rGarden: "Сад между нами",
  rGardenSub: "то, что вы выращиваете вместе",

  /* ── Vandaag-hero ─────────────────────────────────────────────────────── */
  heroKicker: "сегодня",
  heroPendingLine: "вопрос дня ждёт тебя",
  heroPendingAction: "ответить",
  heroPendingNote: "открывается только вместе",
  heroWaitingLine: "твоя звезда горит — ждём вторую",
  heroWaitingAction: "посмотреть",
  heroWaitingNote: "ответ запечатан до встречи",
  heroRevealAction: "открыть день",
  heroRevealNote: "вы оба здесь",
  todayBridgeAria: "что можно сделать после сегодняшнего дня",
  todayBridgeKicker: "после сегодняшнего",
  todayBridgeTitle: "оставить здесь ещё один след",
  todayBridgeMail: "написать письмо",
  todayBridgePlay: "сыграть раунд",
  todayBridgeOpenMail: (n: number) => `открыть письмо · ${n}`,
  todayBridgeMailWaiting: (n: number) => `${n} ждёт на полке`,
  todayBridgeMailTraveling: (n: number) => `${n} в пути по городу`,
  todayBridgeOpenPlay: (n: number) => `продолжить раунд · ${n}`,
  todayBridgePlayWaiting: (n: number) => `${n} открытых раунд${n === 1 ? "" : "а"}`,
  cityAfterglowAria: "что только что изменилось в городе",
  cityAfterglowLetter: "письмо только что двинулось",
  cityAfterglowLetterDetail: "Город несёт его дальше. Можно заглянуть на почту.",
  cityAfterglowGame: "у игры появился финал",
  cityAfterglowGameDetail: "Общий раунд оставил след в Аркаде.",
  cityAfterglowOpenMail: "открыть почту",
  cityAfterglowOpenArcade: "зайти в Аркаду",
  moodCurrent: "Текущее настроение",
  moodTitle: "настроение",
  moodYou: "ты",
  moodNotSet: "не выбрано",
  moodWaiting: "ждём",
  moodSetThisHour: "Выбрать настроение на этот час",
  moodSetTo: (mood: string) => `Выбрать настроение: ${mood}`,
  moodUnknown: "неизвестно",
  moodCalm: "спокойно",
  moodFocused: "собранно",
  moodSoft: "нежно",
  moodTired: "устало",
  moodLow: "грустно",
  moodStressed: "напряжённо",
  moodExcited: "в предвкушении",
  moodPlayful: "игриво",
  careAria: "маленькая забота о себе",
  careKicker: "немного заботы",
  careTitle: "что уже получилось сегодня",
  careNote: "без целей · просто отметка",
  careWater: "вода",
  careMeal: "еда",
  careAddWater: "добавить воду",
  careAddMeal: "отметить еду",
  careUndoWater: "убрать воду",
  careUndoMeal: "убрать еду",
  carePartnerSummary: (name: string, water: number, meals: number) =>
    `${name} сегодня позаботилась о себе · вода ${water} · еда ${meals}`,
  careFailed: "отметка не дошла до города — попробуй ещё раз",

  /* ── navigatie en knoppen ─────────────────────────────────────────────── */
  back: "← назад",
  close: "закрыть",
  open: "открыть",
  send: "отправить",
  cancel: "отмена",
  save: "сохранить",
  city: "Город",
  todayTab: "Сегодня",
  mailTab: "Почта",
  /* De deur tussen de twee shells. Eigennaam blijft Russisch, ook in het
     Engels -- het zijn plekken in deze stad, geen functies. */
  toCity: "Город",

  /* ── verbindingsheader ────────────────────────────────────────────────── */
  connection: "Связь",
  here: "здесь",
  notInCity: "не в городе",
  connTogether: "вы здесь вдвоём",
  /* Een tijdzone is een land: de eigen stad is daarom met de hand te zetten. */
  /* ── Ставка: de inzet ─────────────────────────────────────────────────── */
  /* ── Дорога: de weg ───────────────────────────────────────────────────── */
  /* ── это устройство: meldingen en toestellen ──────────────────────────── */
  /* ── пока тебя не было ────────────────────────────────────────────────── */
  /* ── de panorama: de werkelijke daggebouwen ───────────────────────────────────────────────────────── */
  panoramaHead: "Город",
  panoramaAlt: (n: number) => `силуэт города: ${n} ${ruPlural(n, "дом", "дома", "домов")}`,
  panoramaNote: (n: number) => `${n} ${ruPlural(n, "день", "дня", "дней")} построено · нажми на дом`,
  panoramaMemory: (kind: string) => ({ lantern: "фонарь, оставленный гореть", postcard: "открытка в стене",
    radio: "голос на крыше", wildflower: "маленький дикий сад" } as Record<string, string>)[kind] ?? "маленькая деталь",
  errNotYetMet: "страница перевернётся, когда вы встретитесь",
  errTripNotOver: "пока вы вместе — глава ещё идёт",
  errChapterOpen: "сначала нужна следующая встреча",
  /* Namen voor velden die alleen een placeholder hadden. Een placeholder
     verdwijnt zodra je typt en telt niet als naam van een veld. */
  sFlightField: (k: string) => ({ code: "номер рейса", from: "откуда",
    to: "куда", dep: "вылет", arr: "прилёт", date: "дата" }[k] ?? k),
  sTicketDate: "дата",
  sPackAdd: "добавить в список",
  sBudgetTotal: "общий бюджет",
  sSpendAdd: "добавить расход",
  sPhraseRu: "фраза по-русски",
  sPhraseEn: "перевод на английский",
  /* Слово на проводе: aanwijzingen in plaats van letters. */
  rLoading: "настраиваем приёмник…",
  pLoading: "открываем ящик…",
  /* ── Кто из нас? ──────────────────────────────────────────────────────── */
  gLanterns: (aan: number, tot: number) =>
    `горит ${aan} из ${tot} ${ruPlural(tot, "фонаря", "фонарей", "фонарей")}`,
  gStreetAsleep: (w: string) => `улица уснула — было «${w}»`,
  gPickLetter: "выбери букву",
  gWholeWord: "слово целиком",
  gWholeWordPh: "рискнуть словом…",
  gDareWord: "Рискнуть",
  gKtoIzNas: "Кто из нас?",
  gKtoGuide: "Партия из пяти вопросов. На каждый — кто из нас это, и что выберет другой. Всё вслепую, пока не ответили оба; угаданный прогноз — три очка, четыре из пяти — бонус.",
  ktoSerie: (n: number) => `наша серия: ${n}`,
  ktoWaiting: (d: number, tot: number) => `твои пять готовы · у другого ${d} из ${tot}`,
  ktoPickWho: "кто из нас?",
  ktoPredict: "а что выберет другой?",
  ktoResult: (e: number, tot: number) => `совпало ${e} из ${tot}`,
  ktoHit: (h: string): string => h === "R" ? "Rick угадал" : "Настя угадала",
  wordPick: "что рисуем?",
  digHead: "Пока тебя не было",
  digSeen: "прочитано",
  digSignal: (n: number) => `она отметилась в ${ruPlural(n, "дне", "днях", "днях")}: ${n}`,
  digReveal: (n: number) => `${ruPlural(n, "созвездие собрано", "созвездия собраны", "созвездий собрано")}: ${n}`,
  digLetter: (n: number) => `${ruPlural(n, "письмо дошло", "письма дошли", "писем дошло")}: ${n}`,
  digOgonek: (n: number) => `огонёк: ${n}`,
  digRoad: (n: number) => `отметок на дороге: ${n}`,
  digWager: (n: number) => `ставок сделано: ${n}`,
  digGame: (n: number) => `партий сыграно: ${n}`,
  digSpark: (n: number) => `совпадений в «Одной улице»: ${n}`,
  digRadio: (n: number) => `новых песен: ${n}`,
  digCapsule: (n: number) => `капсул открыто: ${n}`,
  digChapter: (n: number) => `новая глава: ${n}`,
  digGazette: (n: number) => `свежая газета: ${n}`,
  devHead: "Это устройство",
  devPush: "Уведомления",
  devPushOn: "включены · созвездие, почта, огонёк · тихо с 23:00 до 8:00",
  devPushOff: "выключены — ты узнаёшь, только когда откроешь город",
  devDenied: "браузер их запретил; включи в настройках сайта",
  devNoPush: "на этом устройстве недоступны",
  devTurnOn: "Включить",
  devTurnOff: "Выключить",
  devThisOne: "это устройство",
  devSeen: (d: string) => `был(а) ${d}`,
  devForget: "забыть устройство",
  roadHead: "Дорога",
  roadToMeet: (n: number) => `${n} ${ruPlural(n, "день", "дня", "дней")} до встречи`,
  roadToPart: (n: number) => n === 0 ? "последний день вместе"
    : `${n} ${ruPlural(n, "день", "дня", "дней")} вместе`,
  roadDone: "дорога пройдена",
  roadDays: (n: number) => `${n} ${ruPlural(n, "день", "дня", "дней")}`,
  roadMeet: (plek: string) => plek ? `встреча · ${plek}` : "встреча",
  /* Bewust «·» en geen «в»: dat vraagt de voorzetselnaamval («в Москве») en
     die valt niet generiek uit een nominatief af te leiden. Liever een vorm
     die altijd klopt dan een zin die bij de helft van de steden fout staat. */
  roadTogether: (plek: string) => plek ? `вместе · ${plek}` : "вместе",
  roadPart: (plek: string) => plek ? `здесь расходимся · ${plek}` : "здесь расходимся",
  roadNone: "дорога ещё не нарисована",
  roadNoneHow: "поставьте дату следующей встречи ниже — и она появится",
  roadMark: "отметить",
  stationDrawer: "всё для поездки",
  sNights: "Ночлег",
  sNightsLead: "Каждая ночь поездки — одно окно. Светится, если есть где спать.",
  sNightsOpen: (n: number) => `${n} ${n === 1 ? "ночь" : "ночей"} без крыши`,
  sNightsAll: "у каждой ночи есть крыша",
  sNightsAir: "в самолёте",
  sStayAdd: "добавить ночлег",
  sStayFrom: "с",
  sStayUntil: "до",
  sStayPlace: "где",
  sStayRemove: "убрать ночлег",
  sPass: "Посадочный талон",
  sPassReady: "всё готово",
  sStamp: { flights: "рейсы", nights: "ночлег", papers: "паспорта",
            luggage: "багаж", window: "окно встречи" } as Record<string, string>,
  /* De afstand tussen jullie. «ещё» want hij loopt terug naar nul. */
  tripApart: (km: string) => `ещё ${km} км`,
  tripTogetherFrom: "от дома",
  tripTogether: "вместе",
  roadMarkPh: "тот рынок у реки…\nодна строка — одна отметка",
  roadMarkMany: (n: number) => `отметить ${n}`,
  roadDoneMark: "сделано",
  roadUndo: "вернуть",
  roadRemove: "убрать",
  /* ── Итог: het slot van een partij ────────────────────────────────────── */
  gItog: "Итог",
  gItogClose: "убрать",
  gItogSolved: "разгадано",
  gItogLost: "улица уснула",
  gItWas: "было",
  gItogParty: "партия сыграна",
  gItogSpark: "общая искра",
  gItogStreet: "две разные улицы",
  gLanternsLeft: (n: number) => `горит ещё ${n} из 7`,
  gWordFrom: (who: string) => `слово · ${who}`,
  gWagersOpened: "Ставки раскрыты",
  wagerTheirs: (who: string) => `ставка · ${who}`,
  gUnseen: (n: number) => `не раскрыто: ${n}`,
  gUnseenOpen: "посмотреть",
  /* ── Витрина: de bewaarde partijen ────────────────────────────────────── */
  gVitrina: "Витрина",
  gVitrinaLead: "то, что вы уже сыграли — каждую партию можно открыть заново",
  gVitrinaEmpty: "Витрина пока пуста — сыграйте первую партию.",
  gSeasonBoard: "Счёт сезона",
  gOpenItog: "открыть итог",
  gSeasonName: (key: string): string => ({
    "before-moscow": "до Москвы", "moscow-cup": "кубок Москвы",
    "afterglow": "послесвечение" }[key] ?? key),
  gLoadingCab: "спускаемся в подвал…",
  gRetired: "Этот старый раунд здесь больше не продолжается.",
  gAbandonRound: "отказаться от раунда",
  gNoOpenRounds: "Открытых раундов нет — выбери автомат.",
  wagerHead: "Ставки",
  wagerLead: "маленькое обещание на каждую партию",
  wagerPlace: "Поставить",
  wagerPh: "кофе в постель…",
  wagerMine: "Твоя ставка",
  wagerTheirsIn: "её ставка сделана — увидишь после партии",
  wagerTheirsOut: "она ещё не поставила",
  wagerToWin: "тебе причитается",
  wagerToPay: (me: string): string => me === "R" ? "ты обещал" : "ты обещала",
  wagerRedeem: "Получить",
  wagerRedeemed: "исполнено",
  wagerLocked: "откроется, когда вы будете в одном городе",
  wagerNone: "ставок пока нет",
  placeEdit: "где я сейчас",
  placePlaceholder: "город",
  placeLocate: "определить по геолокации",
  /* De plank met gesloten hoofdstukken in de klokkentoren. */
  bookShelf: "книги глав",
  ptHead: "оборот страницы",
  ptName: "имя новой главы",
  ptTurn: "повернуть ключ",
  ptWaiting: "твой ключ повёрнут — ждём второй",
  ptConfirm: "повернуть свой ключ",
  bookClosed: (d: string) => `закрыта ${d}`,
  bookDays: (n: number) => `${n} дней построено`,
  bookOpened: (n: number) => `${n} открыто вместе`,
  bookTogether: (n: number) => `${n} в одном городе`,
  bookLetters: (n: number) => `${n} писем дошло`,
  ogonek: "Огонёк",
  ogonekHint: "постучать в окно",
  wxClear: "ясно",
  wxCloud: "облачно",
  wxRain: "дождь",
  wxSnow: "снег",
  wxStorm: "гроза",
  wxFog: "туман",

  /* ── archief ──────────────────────────────────────────────────────────── */
  archive: "Архив",
  archiveEmpty: "Город ещё пуст. Дни появляются, когда день закрыт.",
  chapters: "Главы",
  years: "Годы",
  months: "Месяцы",
  days: "Дни",
  noChapter: "Без главы",
  daysCount: (n: number) => `${n} дней`,
  mJan: "январь", mFeb: "февраль", mMar: "март", mApr: "апрель",
  mMay: "май", mJun: "июнь", mJul: "июль", mAug: "август",
  mSep: "сентябрь", mOct: "октябрь", mNov: "ноябрь", mDec: "декабрь",

  /* ── chips en meldingen ───────────────────────────────────────────────── */
  offline: "нет сети",
  offlineHint: "всё сохранится и уйдёт позже",
  updateReady: "новая версия",
  updateApply: "обновить",
  updateLater: "позже",
  updateBusy: "сначала закончим — обновим потом",
  updateReadyLong: "новая версия готова",
  /* Waaróm een update nog niet doorgaat. Stond in het Nederlands op het
     scherm -- Ricks taal, niet die van hen. */
  refusalInput: "сначала допиши",
  refusalOutbox: "ждём отправки",
  refusalRedeem: "чуть позже",
  refusalGeneric: "сейчас не вышло",
  offlineChip: "офлайн · всё сохранится",
  queued: (n: number) => `${n} в очереди`,

  /* ── fouten ───────────────────────────────────────────────────────────── */
  errNoAccess: "Нет доступа",
  errMailLoad: "Не открылось",
  errGeneric: "Что-то пошло не так",
  errServiceUnavailable: "Сервис временно недоступен — попробуйте ещё раз или проверьте связь",
  confirmSure: "точно?",
  confirmYes: "да",
  confirmNo: "нет",
  rRemoveTrack: "убрать из эфира",
  errOffline: "Нет сети — попробуем позже",
  errConflict: "Кто-то изменил это раньше",

  /* ── taalschakelaar ───────────────────────────────────────────────────── */
  language: "Язык",
  langRu: "Русский",
  langEn: "Английский",

  /* ── toegankelijkheidslabels (niet zichtbaar, wel voorgelezen) ────────── */
  aInterior: "Интерьер",
  aOpenLetter: "открыть письмо",
  aBackTo: (what: string) => `назад в ${what}`,
  aOpenDay: (date: string) => `открыть день ${date}`,
  aBothWindows: "оба окна",
  aUnreadCount: (n: number) => `${n} непрочитанных писем`,

  /* ── kamerkoppen: eigennaam + wat het is ─────────────────────────────── */
  rObservatory: "Обсерватория",
  /* Небо — het archief als hemel: elk verzegelde etmaal één sterrenbeeld. */
  rSky: "Небо",
  memPlaceholder: "слово, которое ты помнишь",
  memSearching: "Ищем…",
  memNothing: "Ничего с этим словом.",
  memDays: "дни",
  memWords: "словарь",
  memLetters: "письма",
  memLetterFound: "письмо с этим словом",
  memBlind: "Твоё — всегда. Её — только в днях, открытых вдвоём.",
  skySub: "каждая ночь — своё созвездие",
  skyLoading: "Собираем небо…",
  skyEmpty: "Пока ни одной запечатанной ночи.",
  skyLegend: "Звёзд столько, сколько следов вы оставили в тот день. Линии горят у ночей, открытых вдвоём.",
  skyNightAria: (day: string, n: number) => `${day}: ${n} следов, открыть день`,
  skyQuietAria: (day: string) => `${day}: тихая ночь, открыть день`,
  eSky: "Небо",
  eSkySub: "все ночи разом",
  rObservatorySub: "обсерватория",
  rPost: "Почта",
  rPostSub: "ночное отделение",
  rRadio: "Радио 2500",
  rRadioSub: "крыша · передатчик между окнами",
  rArcade: "Аркада",
  rArcadeSub: "неоновый подвал",
  rAtelier: "Crincident",
  rAtelierSub: "ателье · making things awkward since 2026",
  rKiosk: "Киоск",
  rKioskSub: "городская газета · проект недели",
  rTower: "Часовая башня",
  rTowerSub: "капсулы времени",
  rStation: "Станция",
  rMuseum: "Город N",
  mDayGoneHint: "Этот день можно найти в Архиве.",

  /* ── Дорога: de reis-tab en het album van onderweg ────────────────────── */
  tripAlbum: "Дни в пути",
  tripAlbumLead: "Каждый день открыт для вас обоих: добавляйте столько снимков, " +
    "сколько захотите.",
  tripAlbumNoDays: "Пока нет рейсов — дни появятся, когда встанет табло.",
  tripAddPhoto: "снимок",
  tripChoosePhoto: "выбрать снимок",
  tripCaption: "подпись",
  tripCaptionPh: "что здесь происходит…",
  tripTagPlace: "отметить место",
  tripOnMap: "на карте",
  tripOpenPhoto: "открыть снимок",
  tripRemovePhoto: "убрать снимок",
  tripPhotoCount: (n: number) => `${n} ${ruPlural(n, "снимок", "снимка", "снимков")}`,
  tripPickDay: "день поездки",
  tripToday: "сегодня",
  tripOpenDay: "открытый день",
  tripPreviousDay: "предыдущий день",
  tripNextDay: "следующий день",
  tripDayOf: (n: number, total: number) => `день ${n} из ${total}`,
  tripDocs: "Документы",
  tripDocsLead: "Паспорт, билеты, бронирования — отдельно от дневного альбома.",
  tripAddDoc: "документ",
  tripChooseDoc: "выбрать файл",
  tripDocCaptionPh: "например, номер брони…",
  tripRemoveDoc: "убрать документ",
  tripDocKind: {
    passport: "паспорт", ticket: "билет", hotel: "бронирование", other: "другое",
  } as Record<"passport" | "ticket" | "hotel" | "other", string>,
  tripExport: "экспорт",
  tripExportTitle: "Дорога · сводка",
  tripExportLead: "Все дни поездки: где вы были, что произошло, фотографии с местом и временем.",
  tripExportBack: "к дням",
  tripExportPrint: "печать / PDF",
  tripExportNoPhotos: "без фотографий",
  tripMapLabel: "карта пути",
  tripMapOpen: "открыть карту",
  tripSlot: { "утро": "утро", "день": "день", "вечер": "вечер" } as Record<string, string>,
  tripVoice: "записать голос",
  tripVoiceStop: "остановить",
  tripRemoveVoice: "убрать запись",
  tripVoiceNoMic: "микрофон недоступен. проверь разрешение и попробуй ещё раз.",
  tripDossierDays: "дней в пути",
  tripDossierPhotos: "снимков",
  tripDossierPlaced: "с местом",
  tripDossierTogether: "задокументированных дней вместе",
  tripDossierSpent: "потрачено",
  tripLandingOn: "дальше в город",
  tripLanded: (plek: string) => `он приземлился · ${plek}`,
  tripLandedKm: (km: number) => `${km} км между вами`,
  tripLandedIn: (n: number) => n <= 1 ? "вот-вот"
    : `через ${n} ${ruPlural(n, "минуту", "минуты", "минут")}`,
  tripQueued: (n: number) => `${n} ${ruPlural(n, "файл ждёт", "файла ждут", "файлов ждут")} отправки`,
  sSpendCat: {
    food: "еда", transport: "транспорт", stay: "жильё", other: "другое",
  } as Record<"food" | "transport" | "stay" | "other", string>,
  sSpendRemove: "убрать расход",

  /* ── Обсерватория: het dagritueel ─────────────────────────────────────── */
  oConstellation: "созвездие дня",
  oBlind: "Отвечай вслепую — открывается только вместе…",
  oSealsAt: "запечатается в 01:00 мск",
  /* De server weigert op vier verschillende, betekenisvolle manieren. Die
     hoorden alle vier «что-то пошло не так» te worden; nu zeggen ze wat er
     werkelijk aan de hand is. */
  errDaySealed: "День уже запечатан — сегодня это не отправить.",
  errAnswerFinal: "Ответ уже записан. Он не меняется — так устроен этот день.",
  errDayRevealed: "День уже открыт вдвоём. Переписать его нельзя.",
  errStale: "Что-то изменилось. Обнови и попробуй снова.",
  /* Het antwoord is onherroepelijk zodra het weg is; één tik is daarvoor te
     weinig drempel. */
  oConfirmAsk: "Отправить ответ? Изменить его потом нельзя.",
  oConfirmYes: "да, отправить",
  oConfirmNo: "ещё подумаю",
  oDraftKept: "черновик сохранён на этом устройстве",
  oUndo: "назад",
  oClear: "стереть",
  /* De uploadkant weigert ook betekenisvol; ook dat hoort een zin te zijn. */
  errRecordEmpty: "Запись получилась пустой. Попробуй ещё раз.",
  errMic: "Не получилось включить микрофон. Проверь доступ к нему.",
  errTooLarge: "Слишком большой файл — не поместился.",
  errFileType: "Такой файл не подходит.",
  /* Eén eerlijke zin per statusklasse; zie apps/web/src/refusal.ts. */
  errNotYours: "Это не твоё — открыть нельзя.",
  errGone: "Этого больше нет.",
  errAlready: "Это уже записано — второй раз не получится.",
  errInvalid: "Так не получится отправить. Проверь, что заполнено.",
  errTooFast: "Слишком часто. Подожди немного.",
  errWaitOther: "Твой ход сохранён. Ждём второго.",
  errRoundOver: "Этот раунд уже закончился.",
  oQuestion: "вопрос",
  oWord: "слово",
  oCityWord: "слово для вывески",
  oLeaveTrace: "Оставить след сегодняшнего дня",
  oTraceOptional: "по желанию · видно вместе после открытия",
  oWordPurpose: "Это слово станет неоновой вывеской сегодняшнего дома.",
  oPhotosRevealed: "фотографии дня",
  oYourPhoto: "твоя фотография дня",
  oPartnerPhoto: (name: string) => `фотография дня · ${name}`,
  oReactPhoto: "оставить стикер на фото",
  oReactionFrom: (name: string) => `стикер от ${name}`,
  oReactionFinal: "Стикер оставлен. Выбор окончательный.",
  echoTitle: "Эхо после открытия",
  echoExplain: "Оставь один тихий знак — без слов.",
  echoLeave: "оставить эхо",
  echoSkip: "можно пропустить",
  echoSent: "Эхо осталось в этом дне.",
  echoPending: "Эхо ждёт связи и появится после синхронизации.",
  echoRetry: "попробовать ещё раз",
  echoFrom: (name: string) => `${name} оставил(а) эхо`,
  oLine: "строка",
  oPhoto: "фото",
  oSmell: "запах",
  oSound: "звук",
  oSong: "песня",
  oDrawing: "рисунок",
  oHis: "его",
  oHers: "её",
  oOptional: "по желанию",
  oLinePh: "одна строка…",
  oWordPh: "слово…",
  oOneFrame: "+ один кадр",
  oPhotoOnce: "кадр можно заменить, пока день не открылся",
  oPhotoPromptTitle: "Фото сегодняшнего дня",
  oPhotoPromptSub: "Один настоящий момент. Вы увидите фотографии друг друга после открытия.",
  oChoosePhoto: "Снять или выбрать фото",
  oPhotoUploading: "Загружается…",
  oPhotoAdded: "Твой кадр ждёт общего открытия.",
  oReplacePhoto: "заменить кадр",
  oRecord: "● запись",
  oStopSend: "■ стоп и отправить",
  oTodayLabel: (what: string) => `${what} · сегодня`,
  /* Stond als halve Russische, halve Engelse zin op het scherm. */
  oWaiting: (who: string) => `Твоя звезда горит. Ждём ${who} — открывается вместе.`,
  oBlindNote1: "Она видит твои звёзды, но не слова.",
  oBlindNote2: "Ответы запечатаны, пока вы оба не закончите. Всё после вопроса — по желанию.",

  /* ── Музей ────────────────────────────────────────────────────────────── */
  mAnswer: "ответ",
  mSealed: " · запечатано",

  /* ── Почта ────────────────────────────────────────────────────────────── */
  pSend: "Отправить",
  pDelivered: "доставлено",
  pRead: "прочитано ♥",
  pLetter: "письмо",
  pCard: "открытка",
  pCardForYou: "открытка к тебе",
  pSentOn: (d: string) => `отправлено ${d}`,
  pYourCard: "твоя открытка",
  pShelfHis: "его полка",
  pShelfHers: "её полка",
  pShelf: (who: string) => `полка · ${who}`,
  pDearN: "Дорогая Н., …",
  pDearR: "Дорогой Р., …",
  pFrom: (who: string) => `от ${who}`,

  /* ── Аркада: de spellen ───────────────────────────────────────────────── */
  gWhereAmI: "Где я? 2.0",
  gMemoryMetro: "Метро памяти",
  gBasement: "неоновый подвал",
  kSeasonKicker: (s: string) => `сезон · ${s}`,
  gSloganPh: "слоган…",
  gHangman: "Виселица",
  gSecretWordPh: "секретное слово…",
  gSealWord: "запечатать слово",
  gOwnWordWaiting: "Твоё слово уже ждёт другого игрока.",
  gHangWaiting: "Другой угадывает твоё слово. Ты увидишь каждую попытку здесь.",
  gHangmanSolved: "слово разгадано",
  gHangmanLost: "попытки закончились",
  gTodayRound: "сегодня",
  gOpenRounds: (n: number) => `в игре · ${n}`,
  gYourTurn: "твой ход",
  gHowItWorks: "как играть",
  gSameStreetGuide: "Выбери свой ответ, затем угадай выбор другого. Правильный прогноз даёт очко Mind Reader; одинаковый выбор зажигает постоянную искру в городе.",
  gRecommended: "сегодняшний автомат",
  gHangGuide: "Ты прячешь слово, другой угадывает по букве — или рискует словом целиком. Каждый промах гасит фонарь; на седьмом улица засыпает. Угадано — очки обоим: чем меньше погасло, тем больше.",
  gCityGameEffect: "Каждая завершённая игра посылает свет по городской оси. Совпадение в «Одной улице» остаётся в городе как искра.",
  gChooseCabinet: "выбери автомат",
  gContinue: "продолжить ход",
  gBackToGames: "← все игры",
  gDailyTag: "раз в день",
  gStartTag: "начать новую",
  /* De woordenschat van de kasten: één stelsel voor alle vier, en alleen
     toestand -- geen beloftes over minuten. */
  kMine: (n: number) => n > 1 ? `твой ход · ${n}` : "твой ход",
  kWaitingFor: (h: string, n = 0): string =>
    `${h === "N" ? "ждём Настю" : "ждём Рика"}${n > 1 ? ` · ${n}` : ""}`,
  kStreetNew: "ещё не играли",
  kStreetDone: "сыграно сегодня",
  kSerieTag: (n: number) => `серия · ${n}`,
  kCabError: "подвал не отвечает",
  kRetry: "повторить",
  kNewParty: "новая партия",
  kStarterWord: "новое слово",
  rrTitle: "Бег по маршруту",
  rrKicker: "ночная дорога",
  rrLead: "шесть сигналов до встречи",
  rrMapLabel: (n: number, total: number) => `маршрут: ${n} из ${total} точек`,
  rrStart: "начало",
  rrArrival: "прибытие",
  rrYourTurn: "твой ход — выбери следующий сигнал",
  rrWaiting: "маршрут ждёт Настю",
  rrSteady: "идти ровно",
  rrSteadyNote: "одна точка вперёд",
  rrScenic: "свернуть к свету",
  rrScenicNote: "одна точка и строка для вас двоих",
  rrShortcut: "короткий путь",
  rrShortcutNote: "две точки, но с крюком",
  rrHistory: "последние сигналы маршрута",
  rrArrived: "вы пришли вместе",
  rrLate: "поздно, но вместе",
  rrFinishedLead: "Маршрут остался в городе как тёплый след.",
  rrNew: "новый маршрут",
  rrStarterLead: "Выберите сигналы по очереди и дойдите до последней звезды.",
  rrGuide: "Это общая дорога на шесть точек. Ходите по очереди: ровный путь, светлый крюк или короткая дорога. Любой финал — ваш общий след, не соревнование.",
  /* Het slot spreekt de inzet uit. De werkwoordsvorm volgt de kijker. */
  sWon: (me: string): string => me === "R" ? "ты выиграл" : "ты выиграла",
  sWonBy: (h: string): string => h === "R" ? "Rick выиграл" : "Настя выиграла",
  sTheyPromised: (h: string): string => h === "R" ? "Rick обещал" : "Настя обещала",
  sYouPromised: (me: string): string => me === "R" ? "ты обещал" : "ты обещала",
  sNoWinner: "никто не выиграл — обещания остаются без хозяина",
  sOwed: "за тобой",
  sUnused: "не понадобилась",
  kSerieUp: (a: number, b: number) => `наша серия: ${a} → ${b}`,
  kSerieBroke: (a: number, b: number) =>
    b ? `серия прервалась на ${a} · теперь ${b}` : `серия прервалась на ${a}`,

  /* ── Часовая башня: capsules ──────────────────────────────────────────── */
  cSeal: "запечатать",
  cOnDate: "на дату",
  cWhenTogether: "когда вместе",
  cWhenTogetherLong: "когда вы вместе онлайн",
  cWhenSad: "когда грустно",
  cNewChapter: "новая глава",
  cSpecialMoment: "особый момент",
  cStillClosed: "ещё закрыта",
  cCanOpen: "можно открыть",
  cYoursWaiting: "твоя · ждёт",
  cDate: "дата",
  cLetterToFuturePh: "письмо в будущее…",
  cAddPhoto: "+ добавить фото",
  cRemovePhoto: "убрать фото",
  cPhotoPreview: "фото для капсулы",
  cPhotoAlt: "фото из капсулы",
  cPhotoSealed: "фото запечатано",
  cSavedOffline: "капсула сохранена и будет отправлена при подключении",

  /* ── Станция ──────────────────────────────────────────────────────────── */
  sDay: "день",
  sPunchR: "компостер R",
  sPunchN: "компостер N",
  sNewTicketPh: "новый билет…",
  sAtGate: "у гейта",
  sPlanToday: "план на сегодня",

  /* ── Радио 2500 ───────────────────────────────────────────────────────── */
  rUntitled: "без названия",
  rWhyPh: "почему именно он…",
  rLinkPh: "ссылка (spotify / яндекс)…",
  rTrackPh: "трек…",
  rStop: "■ стоп",
  rVoiceIntro: "● голос-интро",
  rHasIntro: "✓ интро",
  rWithBridge: " · со звуковым мостом",
  rChooseSource: "источник радио",
  rCityRadio: "Радио города",
  rSpotifyFeed: "Spotify-эфир",
  rSharedPlaylist: "Что вы слушали",
  rSpotifyLive: "из Spotify · у каждого своя половина",
  rSpotifyOffline: "Плейлист живёт в Spotify. Без сети его не показать — городское радио ниже работает и офлайн.",
  rSpNotLinkedWho: (who: string) => `${who} ещё не подключил(а) Spotify.`,
  rSpNoPlays: "Пока тихо. Здесь появится то, что вы слушали.",
  rSpNotLinkedShort: "Spotify не подключён",
  rSpPaused: "синхронизация остановлена",
  rSpNeverSyncedShort: "ещё не синхронизировано",
  rSpStaleShort: (when: string) => `Spotify недоступен · от ${when}`,
  rSpFailedShort: "синхронизация не удалась",
  rSpLastPlayed: (when: string) => `последний трек ${when}`,
  rSpQuiet: "пока тихо",
  rSpQuietDay: "Сегодня ещё тихо. Здесь появится то, что вы слушали.",
  rSpSharedHours: (n: number) =>
    `вы совпали ${n} ${n === 1 ? "час" : n < 5 ? "часа" : "часов"}`,
  rSpNoSharedHours: "сегодня вы ещё не совпали ни в одном часе",
  rSearching: "ищем в Spotify…",
  rNoHits: "Ничего не нашлось. Впиши как есть — так тоже хорошо.",
  rPickTrack: "выбрать трек",
  rPickHint: "Начни печатать название — можно выбрать из Spotify.",
  rSpOneTick: "каждая черта — один трек",
  rSpGap: (from: string, to: string) => `${from} — ${to} · тихо`,
  rSpGapOne: (at: string) => `${at} · тихо`,
  rSpOpen: "открыть в Spotify",
  rSpotifyExplain: "Имя рядом с треком — не догадка: каждая строка приходит из своего аккаунта. Плейлист «Наши» открывается ниже, прямо в Spotify.",
  rOpenSpotify: "открыть в Spotify",

  /* ── Киоск ────────────────────────────────────────────────────────────── */
  kYourVote: " · твой голос",
  kBuilt: " — построено: ",
  kChooseTogether: "Город предлагает три проекта. Выберите один — вместе.",
  kVoteIn: "Твой голос учтён — ждём второй.",
  kYourPartPh: "твоя часть проекта…",

  /* ── Ателье Crincident ────────────────────────────────────────────────── */
  aTee: "футболка",
  aHoodie: "худи",
  aCap: "кепка",
  aTote: "тоут",
  aJustBecause: "general fatigue",
  aContextPh: "отдел (general fatigue, escalations…)",

  /* ── overige kamerteksten ──────────────────────────────────────────── */
  gOnAirWaiting: "Ты в эфире — ждём вторую волну.",
  gSloganComing: "Слоган в пути…",
  gToAir: "в эфир",
  gFourDates: "4 общие даты — в правильном порядке",
  gLetsGo: "поехали",
  gSeasonClean: "Сезон ещё чист.",
  aOtherWrites: "другая половина пишет слоган, не видя вещи",
  aToMachine: "в машину",
  aShowcase: "Витрина · мокапы",
  kProjectOfWeek: "Проект недели",
  kBuiltTogether: "построено вместе",
  kYourPartReady: "Твоя часть готова — ждём вторую половину.",
  kSubmit: "внести — submit",
  kLoading: "киоск открывается",
  kLoadingExplain: "Проверяем проекты и выпуски — ничего не показываем до ответа города.",
  kErrorLabel: "киоск не отвечает",
  kErrorExplain: "Содержимое не изменилось. Повтори загрузку, когда связь вернётся.",
  kFirstRunLabel: "первый визит",
  kFirstRunTitle: "Здесь начнётся следующая история.",
  kFirstRunExplain: "Киоск хранит ваши общие проекты и выпуски газеты. Новое появится здесь, когда город подготовит его — без пустых карточек и выдуманных данных.",
  kFirstRunAction: "открыть сегодня",
  kMondayBrings: "Понедельник принесёт новые проекты.",
  kGazette: "Газета",
  kFirstIssue: "Первый выпуск выходит в воскресенье — сам.",
  kHowItWorks: "как работает киоск",
  kFlowVote: "1 · тайное голосование",
  kFlowContribute: "2 · по одной части",
  kFlowBuild: "3 · новое место в городе",
  kVoteExplain: "Каждый выбирает один проект вслепую. Когда проголосовали оба, совпадение побеждает; при разных выборах город выбирает один из ваших двух проектов.",
  kVoteMarked: "Твой выбор запечатан. Его можно изменить, пока голосует другой; чужой голос остаётся скрытым.",
  kContributeExplain: "После выбора каждый добавляет свою часть: текст, фото или звук. Части другого скрыты до завершения.",
  kBuildExplain: "Когда обе части готовы, проект становится постоянным зданием в Архивном квартале и попадает в газету.",
  kCurrentStep: "сейчас",
  kLatestIssue: "свежий выпуск",
  kOlderIssues: (n: number) => `старые выпуски · ${n}`,
  mDictOfDay: "Словарь этого дня",
  oOneStroke: "одним штрихом или сотней",
  oDone: "Созвездие собрано.",
  oSoundDone: "Звук дня записан.",
  oDrawingDone: "Рисунок дня готов.",
  oSendIt: "отправить — send",
  pArrived: "Прибыло · arrived",
  pEmptyYet: "пока пусто",
  pTravelling: "Лента · travelling",
  pWrite: "Написать · write",
  pInstant: "сразу",
  pQuick: "быстро · ±30 сек",
  pMedium: "сегодня · 6–18 ч",
  pSlow: "медленно · 2–5 дней",
  pRandom: "город решает",
  pWindowFrom: "не раньше",
  pWindowTo: "не позже",
  pRandomNote: "Город тайно выберет момент внутри окна.",
  pCityDecides: "город выберет момент",
  pSomewhereBetween: (a: string, b: string) => `когда-то между ${a} и ${b}`,
  pCardsTravel: "Открытки едут по карте — виден конверт, не текст.",
  pSent: "Отправлено",
  rBothCities: "2500 kHz · оба города в эфире",
  rBroadcasts: "Выпуски · broadcasts",
  rCassettes: "Кассеты · tracks",
  rListen: "▶ слушать",
  rEmpty: "Эфир пуст. Первый трек — твой.",
  rAddToAir: "В эфир · add",
  sBoard: "Табло · departures",
  sEasyDay: "Сегодня лёгкий день. Только всё зелёное — остальное подождёт.",
  sTickets: "Билеты · два компостера = грин-лайт",
  sPlans: "Наши планы",
  sPlansExplain: "Один предлагает план. Каждый ставит свой компостер; два компостера добавляют его в Today Pass.",
  sNoPlans: "Пока нет планов. Добавь первый — второй человек сможет подтвердить его.",
  sTimeOfDay: "время дня",
  sMorning: "утро",
  sAfternoon: "день",
  sEvening: "вечер",
  sMaintenance: "обслуживание и старый импорт",
  sLuggage: "Багаж · бюджет · фразы",
  sSuitcase: "чемодан",
  sLeft: "осталось",
  sPhrases: "фразы",
  sCalendar: "календарь · .ics",
  sCalendarSaved: "календарь сохранён",
  sImportOld: "импорт из старого планировщика",
  sPasteJson: "вставь JSON-экспорт (двух устройств — по очереди)",
  sDupIgnored: "дубли игнорируются · append-only",
  sImport: "импорт",
  cEmpty: "Башня пуста — запечатай первую капсулу.",
  cOpened: "Открытые",
  cSealCapsule: "запечатать капсулу — seal",
  cSheSeesLock: "она увидит замок, не текст",
  sChapterTwo: "ГЛАВА II · ······",
  sNotYet: "ЕЩЁ НЕТ",
  sLessons: "Уроки Рика",
  sLessonsLead: "Когда он занят. Время московское, рядом — его.",
  sLessonsNone: "На ближайшие две недели уроков нет.",
  sLessonNow: (tot: string) => `Сейчас урок — до ${tot} по Москве.`,
  sLessonsStale: (when: string) => `расписание от ${when}`,
  sLessonsFailed: "расписание не удалось обновить",
  sMsk: "МСК",
  sHisTime: "у него",
  sTrip: "Дата встречи",
  sTripExplain: "Поставь дату на табло — и город начнёт обратный отсчёт. За трое суток до неё квартал переходит «навстречу», и открываются Ворота.",
  sTripNone: "Даты на табло пока нет.",
  sTripMeet: "встречаемся",
  sTripEnd: "расстаёмся",
  sTripSave: "на табло",
  sTripClear: "убрать дату",
  sTripBad: "Конец должен быть позже начала.",
  sGate: "Ворота",
  sGateExplain: "Два ключа, повёрнутые в течение полутора минут друг от друга. Только так глава становится «вместе» — ни один из вас не может решить это в одиночку.",
  sGateTurn: "повернуть свой ключ",
  sGateWaiting: "Твой ключ повёрнут. Ждём второй — в течение 90 секунд.",
  sGateTogether: "Вы вместе. Город это знает.",
  pArrivesAt: (eta: string) => `прибудет ~${eta}`,
  pOnWayEta: (eta: string) => `в пути · ~${eta}`,
  mNeon: (text: string) => `неон: «${text}»`,
  mCityMemory: (kind: string) => ({ lantern: "здесь оставили свет", postcard: "этот день оставил открытку",
    radio: "на крыше ещё слышен голос", wildflower: "день оставил маленький сад" } as Record<string, string>)[kind] ?? "день оставил маленькую деталь",
  mCityBuiltBy: (names: string[]) => `построено вместе: ${names.join(" · ")}`,
  cityBuildPending: "город достраивается · проверь связь",
  cityBuildRetry: "проверить ещё раз",
  cityBuildFresh: "город это увидел · здание уже в городе",
  mPartnerLeft: (who: string, n: number) => `${who} оставил(а) здесь ${n} сигнал(а) — откроются вместе.`,
  mMailThatDay: (n: number) => `В этот день пришла почта: ${n}.`,
  kLandmark: (title: string) => `«${title}» — теперь ориентир в городе.`,
  kChosen: (title: string) => `выбран · ${title}`,
  kNewWords: (w: string) => `новые слова: ${w}`,
  kOnAirNow: (title: string) => ` — в эфире: ${title}`,
  kPostStats: (delivered: number, travelling: number) => `почта: ${delivered} доставлено · ${travelling} в пути`,
  kProjectLabel: (title: string) => ` — проект: ${title}`,
  rWeekAir: (week: string) => `${week} · в эфире`,
  rTrackCount: (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? "трек" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "трека" : "треков"}`,
  gWhatOnIt: (g: string) => `${g} — а что на нём? Пиши вслепую.`,
  gCabinet: (name: string) => `Кабинет дня · ${name}`,
  aColor: (c: string) => `цвет ${c}`,
  gFromRealDays: "из настоящих открытых дней",
  gSameStreet: "Одна улица",
  gSketchRelay: "Слепая эстафета",
  ssKicker: "ежедневная игра · меньше минуты",
  ssChooseMine: "Что выберешь ты?",
  ssChooseTheirs: "Что выберет другой?",
  ssSeal: "запечатать два ответа",
  ssSealed: "Твои ответы запечатаны",
  ssWaiting: "Ждём второе окно — ответы пока скрыты.",
  ssTheirChoice: "выбор",
  ssTheirPrediction: "предсказание",
  ssMindReader: "читатель мыслей +1",
  ssMissed: "не угадал(а)",
  ssSpark: "Одинаковый выбор — в городе зажглась общая искра\u00a0✦",
  ssDifferent: "Две разные улицы. Завтра будет новый вопрос.",
  ssScore: "Читатели мыслей",
  ssTodayDone: "сегодня сыграно",
  ssOnline: "Для запечатывания нужна сеть.",
  cDaysBuilt: (n: number) => `${n} домов`,
  cUntil: (when: string) => `до ${when}`,
  cCapsuleAria: (cond: string) => `капсула · ${cond}`,
  sWeAreTired: "мы устали — сделать день легче",
  sUndoTired: "мы снова в силах — вернуть планы",
  sTodayPass: "Пропуск на сегодня",
  sRemovePlan: (title: string) => `удалить план: ${title}`,
  dkTogether: "вдвоём",
  dkConstruction: "стройка",
  dkDark: "тихо",
  dkPark: "парк",
  dkAlley: "переулок",
  toastWindowLit: (from: string) => `✦ Огонёк от ${from} только что зажёг твоё окно.`,
  toastOgonekSent: (to: string) => `✦ Огонёк отправлен для ${to}: окно загорится, и может прийти тихое уведомление.`,
  toastOgonekFailed: "Огонёк не дошёл — проверь соединение и попробуй снова.",

  gCringeMachine: "Машина Crincident",
  aShowcaseEmpty: "Витрина ждёт первую вещь.",

  aMachine: "Машина · выбери вещь, слоган придёт вслепую",
  aStepOne: "шаг 1 из 2 · собери вещь",
  aStepTwo: "шаг 2 из 2 · слоган вслепую",
  aHiddenDetails: "Цвет и контекст откроются только после слогана.",
  aWaitingSlogan: "Вещь отправлена. Ждём слоган из другого окна.",
  aReveal: "открыть вещь",
};

/** De vorm van het woordenboek. `en` moet hier exact aan voldoen. */
export type Dict = typeof ru;
export type Locale = "ru" | "en";

/* Engels. Functienamen zijn Engels; eigennamen blijven Russisch en worden in
   de UI klein en secundair getoond -- de stad heet Почта, ook als je hem in
   het Engels leest. */
const en: Dict = {
  dSvyazi: "Connection Square",
  dSvyaziSub: "where you meet",
  dSignal: "Signal Quarter",
  dSignalSub: "the things that sound",
  dArchive: "Archive Quarter",
  dArchiveSub: "the things that stayed",
  dVokzal: "Station Quarter",
  dVokzalSub: "the things ahead",

  bothHere: "you're both here",
  quiet: "quiet",
  questionOfDay: "question of the day",
  waitingSecondStar: "waiting for the second star",
  constellationDone: "constellation complete ✦",
  unread: (n: number) => `${n} unread`,
  onTheWay: (pct: number) => `on its way · ${pct}%`,
  oneSkyOpen: "one sky · open it",
  oneSky: "one sky",
  skyTitle: "One sky",
  skyPlace: "place a star",
  skyNote: (seconds: number) => `One sky for two · ${seconds}s. Near-simultaneous touches become meteors. The constellation stays.`,
  skyRemaining: (n: number) => `your stars left today: ${n}`,
  skyFull: "Your part of tonight's sky is complete. The constellation stays; tomorrow has fresh space.",
  skySendFailed: "The star did not arrive. Check the connection and try again.",
  letterOnTheWay: "a letter is on its way",
  youHomeSheNot: "your partner is offline",
  daysBuilt: (n: number) => `${n} days built`,
  daysShort: (n: number) => `${n} days`,
  emptyYet: "nothing yet",
  /* ── departure board: until you meet ─────────────────────────────────── */
  cdDays: (n: number) => `${n} ${n === 1 ? "day" : "days"} until you meet`,
  cdHours: (n: number) => `${n} ${n === 1 ? "hour" : "hours"} until you meet`,
  cdTogether: "you're together",
  chState: (s: string): string => ({ APART: "apart", APPROACH: "approaching",
    TOGETHER: "together", AFTERGLOW: "afterglow", ARCHIVE: "archive" } as
    Record<string, string>)[s] ?? s.toLowerCase(),
  mastDay: (n: number) => `day ${n}`,
  gcTitle: "Central Garden",
  gcSub: "the green heart of the city",
  msNewStreet: (ch: string) => `${ch} opened a new street`,
  projectWing: "Project House",
  projectsBuilt: (n: number) => `${n} ${n === 1 ? "project" : "projects"} built`,
  railHint: "swipe sideways to see more →",
  posterRowAria: "city posters — days with photographs",
  posterProv: (when: string) => `Built from ${when} · your photo day`,
  onAir: "on air",
  cabinetOfDay: "cabinet of the day",
  window: "shop window",
  weekProject: "project of the week",
  sealed: "sealed",
  twoPunches: "two punches",

  eToday: "Today",
  eTodaySub: "Сегодня",
  ePost: "Post Office",
  ePostSub: "Почта",
  eWindows: "Windows",
  eWindowsSub: "Together online",
  eRadio: "Radio 2500",
  eRadioSub: "Радио 2500",
  eArcade: "Arcade",
  eArcadeSub: "Аркада",
  eAtelier: "Crincident",
  eAtelierSub: "ателье",
  eMuseum: "Archive",
  eMuseumSub: "Архив",
  eKiosk: "Kiosk",
  eKioskSub: "Киоск",
  eTower: "Clock Tower",
  eTowerSub: "Часовая башня",
  eStation: "Station",
  eTickets: "Tickets",
  eTicketsSub: "Билеты · planner & Today Pass",
  eNew: "NEW",
  eNewSub: "another side of the city",
  eNewMeta: "open the new view",

  gKicker: "growing with you",
  gTitle: "The Garden Between Us",
  gSubtitle: "every shared day leaves something alive",
  gOpen: "enter the garden",
  gStages: ["seed", "first sprout", "young garden", "garden of light",
            "in full bloom", "evergreen"],
  gStageNow: (stage: string) => `now · ${stage}`,
  gNext: (n: number) => `${n} more growth points until the next form`,
  gGrowthHelp: "A shared day adds 4 growth points; a delivered letter or sealed capsule adds 3. Opening a capsule, sharing a song or a shared spark adds 2. A new chapter adds 12. The garden grows at your pace.",
  gGrowthTitle: "How the garden grows",
  gComplete: "the garden keeps growing beyond its final form",
  gBenchTitle: "Memory bench",
  gBenchSub: (n: number) => `${n} shared days are waiting here`,
  gBenchOpen: "take us somewhere",
  gDays: "days",
  gLetters: "letter lanterns",
  gCapsules: "capsules",
  gSongs: "song fireflies",
  gSparks: "shared sparks",
  gLegendDay: "days become leaves",
  gLegendLetter: "letters light lanterns",
  gLegendCapsule: "capsules become buds and flowers",
  gLegendSong: "songs release fireflies",
  gLegendSpark: "matching choices light sparks",
  rGarden: "The Garden Between Us",
  rGardenSub: "what the two of you are growing",

  heroKicker: "today",
  heroPendingLine: "the question of the day is waiting for you",
  heroPendingAction: "answer",
  heroPendingNote: "it only opens together",
  heroWaitingLine: "your star is lit — waiting for the second",
  heroWaitingAction: "look",
  heroWaitingNote: "the answer stays sealed until you meet",
  heroRevealAction: "open the day",
  heroRevealNote: "you're both here",
  todayBridgeAria: "what to do after today",
  todayBridgeKicker: "after today",
  todayBridgeTitle: "leave one more trace here",
  todayBridgeMail: "write a letter",
  todayBridgePlay: "play a round",
  todayBridgeOpenMail: (n: number) => `open a letter · ${n}`,
  todayBridgeMailWaiting: (n: number) => `${n} waiting on the shelf`,
  todayBridgeMailTraveling: (n: number) => `${n} travelling through the city`,
  todayBridgeOpenPlay: (n: number) => `continue a round · ${n}`,
  todayBridgePlayWaiting: (n: number) => `${n} open ${n === 1 ? "round" : "rounds"}`,
  cityAfterglowAria: "what just changed in the city",
  cityAfterglowLetter: "a letter just moved",
  cityAfterglowLetterDetail: "The city is carrying it onward. You can visit the post office.",
  cityAfterglowGame: "a game just found its ending",
  cityAfterglowGameDetail: "Your shared round left a trace in the Arcade.",
  cityAfterglowOpenMail: "open the post office",
  cityAfterglowOpenArcade: "visit the Arcade",
  moodCurrent: "Current moods",
  moodTitle: "mood",
  moodYou: "you",
  moodNotSet: "not set",
  moodWaiting: "waiting",
  moodSetThisHour: "Set this hour's mood",
  moodSetTo: (mood: string) => `Set mood to ${mood}`,
  moodUnknown: "unknown",
  moodCalm: "calm",
  moodFocused: "focused",
  moodSoft: "soft",
  moodTired: "tired",
  moodLow: "low",
  moodStressed: "stressed",
  moodExcited: "excited",
  moodPlayful: "playful",
  careAria: "small self-care check-in",
  careKicker: "a little care",
  careTitle: "what has happened for you today",
  careNote: "no goals · just a record",
  careWater: "water",
  careMeal: "food",
  careAddWater: "add water",
  careAddMeal: "mark food",
  careUndoWater: "remove water",
  careUndoMeal: "remove food",
  carePartnerSummary: (name: string, water: number, meals: number) =>
    `${name} has cared for herself today · water ${water} · food ${meals}`,
  careFailed: "the check-in did not reach the city — try again",

  back: "← back",
  close: "close",
  open: "open",
  send: "send",
  cancel: "cancel",
  save: "save",
  city: "City",
  toCity: "Город",
  todayTab: "Today",
  mailTab: "Post",

  connection: "Connection",
  here: "here",
  notInCity: "not in the city",
  connTogether: "you are both here",
  panoramaHead: "The city",
  panoramaAlt: (n: number) => `the city skyline: ${n} ${n === 1 ? "building" : "buildings"}`,
  panoramaNote: (n: number) => `${n} ${n === 1 ? "day" : "days"} built · tap a building`,
  panoramaMemory: (kind: string) => ({ lantern: "a light left on", postcard: "a postcard in the wall",
    radio: "a voice on the roof", wildflower: "a little wild garden" } as Record<string, string>)[kind] ?? "a small detail",
  errNotYetMet: "the page turns when you meet",
  errTripNotOver: "you are still together — the chapter is running",
  errChapterOpen: "there needs to be a next meeting first",
  sFlightField: (k: string) => ({ code: "flight number", from: "from",
    to: "to", dep: "departure", arr: "arrival", date: "date" }[k] ?? k),
  sTicketDate: "date",
  sPackAdd: "add to the list",
  sBudgetTotal: "total budget",
  sSpendAdd: "add an expense",
  sPhraseRu: "phrase in Russian",
  sPhraseEn: "translation in English",
  rLoading: "tuning in…",
  pLoading: "opening the box…",
  gLanterns: (aan: number, tot: number) => `${aan} of ${tot} lanterns burning`,
  gStreetAsleep: (w: string) => `the street fell asleep — it was «${w}»`,
  gPickLetter: "pick a letter",
  gWholeWord: "the whole word",
  gWholeWordPh: "risk the word…",
  gDareWord: "Risk it",
  gKtoIzNas: "Who of us?",
  gKtoGuide: "A match of five questions. For each: which of us is it, and what will the other pick? All blind until both are done; a correct prediction is three points, four of five earns the bonus.",
  ktoSerie: (n: number) => `our streak: ${n}`,
  ktoWaiting: (d: number, tot: number) => `your five are in · the other: ${d} of ${tot}`,
  ktoPickWho: "which of us?",
  ktoPredict: "and what will the other pick?",
  ktoResult: (e: number, tot: number) => `matched ${e} of ${tot}`,
  ktoHit: (h: string): string => h === "R" ? "Rick called it" : "Настя called it",
  wordPick: "what are we drawing?",
  digHead: "While you were away",
  digSeen: "mark as read",
  digSignal: (n: number) => `she checked in on ${n} ${n === 1 ? "day" : "days"}`,
  digReveal: (n: number) => `${n} ${n === 1 ? "constellation" : "constellations"} came together`,
  digLetter: (n: number) => `${n} ${n === 1 ? "letter" : "letters"} arrived`,
  digOgonek: (n: number) => `огонёк: ${n}`,
  digRoad: (n: number) => `${n} ${n === 1 ? "note" : "notes"} on the road`,
  digWager: (n: number) => `${n} ${n === 1 ? "wager" : "wagers"} staked`,
  digGame: (n: number) => `${n} ${n === 1 ? "round" : "rounds"} finished`,
  digSpark: (n: number) => `${n} ${n === 1 ? "match" : "matches"} in One Street`,
  digRadio: (n: number) => `${n} new ${n === 1 ? "song" : "songs"}`,
  digCapsule: (n: number) => `${n} ${n === 1 ? "capsule" : "capsules"} opened`,
  digChapter: (n: number) => `a new chapter: ${n}`,
  digGazette: (n: number) => `a fresh gazette: ${n}`,
  devHead: "This device",
  devPush: "Notifications",
  devPushOn: "on · constellation, post, огонёк · quiet 23:00–08:00",
  devPushOff: "off — you only find out when you open the city",
  devDenied: "the browser blocked them; allow in site settings",
  devNoPush: "not available on this device",
  devTurnOn: "Turn on",
  devTurnOff: "Turn off",
  devThisOne: "this one",
  devSeen: (d: string) => `last seen ${d}`,
  devForget: "forget this device",
  roadHead: "The road",
  roadToMeet: (n: number) => `${n} ${n === 1 ? "day" : "days"} until we meet`,
  roadToPart: (n: number) => n === 0 ? "last day together"
    : `${n} ${n === 1 ? "day" : "days"} together`,
  roadDone: "the road is behind us",
  roadDays: (n: number) => `${n} ${n === 1 ? "day" : "days"}`,
  roadMeet: (plek: string) => plek ? `we meet · ${plek}` : "we meet",
  roadTogether: (plek: string) => plek ? `together · ${plek}` : "together",
  roadPart: (plek: string) => plek ? `we part here · ${plek}` : "we part here",
  roadNone: "the road is not drawn yet",
  roadNoneHow: "set the date of the next meeting below and it appears",
  roadMark: "add a note",
  stationDrawer: "everything for the trip",
  sNights: "Where you sleep",
  sNightsLead: "Every night of the trip is one window. Lit if there is a bed.",
  sNightsOpen: (n: number) => `${n} night${n === 1 ? "" : "s"} without a bed`,
  sNightsAll: "every night has a bed",
  sNightsAir: "in the air",
  sStayAdd: "add a place to sleep",
  sStayFrom: "from",
  sStayUntil: "until",
  sStayPlace: "where",
  sStayRemove: "remove this stay",
  sPass: "Boarding pass",
  sPassReady: "all set",
  sStamp: { flights: "flights", nights: "beds", papers: "passports",
            luggage: "luggage", window: "reunion window" } as Record<string, string>,
  tripApart: (km: string) => `${km} km still between you`,
  tripTogetherFrom: "from home",
  tripTogether: "together",
  roadMarkPh: "that market by the river…\none line, one note",
  roadMarkMany: (n: number) => `add ${n} notes`,
  roadDoneMark: "done",
  roadUndo: "undo",
  roadRemove: "remove",
  gItog: "The finish",
  gItogClose: "put it away",
  gItogSolved: "solved",
  gItogLost: "the street fell asleep",
  gItWas: "it was",
  gItogParty: "revealed",
  gItogSpark: "a shared spark",
  gItogStreet: "two different streets",
  gLanternsLeft: (n: number) => `${n} of 7 lanterns still lit`,
  gWordFrom: (who: string) => `word · ${who}`,
  gWagersOpened: "The wagers are open",
  wagerTheirs: (who: string) => `wager · ${who}`,
  gUnseen: (n: number) => `not opened yet: ${n}`,
  gUnseenOpen: "look",
  gVitrina: "Showcase",
  gVitrinaLead: "what you have already played — every round opens again",
  gVitrinaEmpty: "The showcase is empty — play the first round.",
  gSeasonBoard: "The season",
  gOpenItog: "open the result",
  gSeasonName: (key: string): string => ({
    "before-moscow": "before Moscow", "moscow-cup": "the Moscow cup",
    "afterglow": "afterglow" }[key] ?? key),
  gLoadingCab: "going down to the basement…",
  gRetired: "This older round can no longer be continued here.",
  gAbandonRound: "give up this round",
  gNoOpenRounds: "No open rounds — pick a cabinet.",
  wagerHead: "Wagers",
  wagerLead: "one small promise per round",
  wagerPlace: "Stake it",
  wagerPh: "coffee in bed…",
  wagerMine: "Your wager",
  wagerTheirsIn: "her wager is in — you'll see it after the round",
  wagerTheirsOut: "she hasn't staked yet",
  wagerToWin: "owed to you",
  wagerToPay: (): string => "you promised",
  wagerRedeem: "Claim",
  wagerRedeemed: "kept",
  wagerLocked: "opens when you are in the same city",
  wagerNone: "no wagers yet",
  placeEdit: "where I am now",
  placePlaceholder: "city",
  placeLocate: "find me by location",
  bookShelf: "chapter books",
  ptHead: "turning the page",
  ptName: "name of the new chapter",
  ptTurn: "turn the key",
  ptWaiting: "your key is turned — waiting for the second",
  ptConfirm: "turn your key",
  bookClosed: (d: string) => `closed ${d}`,
  bookDays: (n: number) => `${n} days built`,
  bookOpened: (n: number) => `${n} opened together`,
  bookTogether: (n: number) => `${n} in one city`,
  bookLetters: (n: number) => `${n} letters arrived`,
  ogonek: "Огонёк",
  ogonekHint: "knock on the window",
  wxClear: "clear",
  wxCloud: "cloudy",
  wxRain: "rain",
  wxSnow: "snow",
  wxStorm: "storm",
  wxFog: "fog",

  archive: "Archive",
  archiveEmpty: "The city is still empty. Days appear once a day is closed.",
  chapters: "Chapters",
  years: "Years",
  months: "Months",
  days: "Days",
  noChapter: "No chapter",
  daysCount: (n: number) => `${n} days`,
  mJan: "January", mFeb: "February", mMar: "March", mApr: "April",
  mMay: "May", mJun: "June", mJul: "July", mAug: "August",
  mSep: "September", mOct: "October", mNov: "November", mDec: "December",

  offline: "offline",
  offlineHint: "everything is kept and sent later",
  updateReady: "new version",
  updateApply: "update",
  updateLater: "later",
  updateBusy: "let's finish first — update after",
  updateReadyLong: "a new version is ready",
  refusalInput: "finish writing first",
  refusalOutbox: "waiting to send",
  refusalRedeem: "in a moment",
  refusalGeneric: "didn't work just now",
  offlineChip: "offline · everything is kept",
  queued: (n: number) => `${n} queued`,

  errNoAccess: "Not allowed",
  errMailLoad: "Couldn't load",
  errGeneric: "Something went wrong",
  errServiceUnavailable: "The service is temporarily unavailable — try again or check your connection",
  confirmSure: "sure?",
  confirmYes: "yes",
  confirmNo: "no",
  rRemoveTrack: "remove from the air",
  errOffline: "No connection — we'll try later",
  errConflict: "Someone changed this first",

  language: "Language",
  langRu: "Russian",
  langEn: "English",

  aInterior: "Interior",
  aOpenLetter: "open letter",
  aBackTo: (what: string) => `back to ${what}`,
  aOpenDay: (date: string) => `open day ${date}`,
  aBothWindows: "both windows",
  aUnreadCount: (n: number) => `${n} unread letters`,

  rObservatory: "Observatory",
  rSky: "The sky",
  memPlaceholder: "a word you remember",
  memSearching: "Searching…",
  memNothing: "Nothing with that word.",
  memDays: "days",
  memWords: "dictionary",
  memLetters: "letters",
  memLetterFound: "a letter carrying that word",
  memBlind: "Yours always. Hers only in days you opened together.",
  skySub: "every night its own constellation",
  skyLoading: "Gathering the sky…",
  skyEmpty: "No sealed night yet.",
  skyLegend: "A night has as many stars as the traces you left that day. The lines are lit on nights you opened together.",
  skyNightAria: (day: string, n: number) => `${day}: ${n} traces, open the day`,
  skyQuietAria: (day: string) => `${day}: a quiet night, open the day`,
  eSky: "The sky",
  eSkySub: "all the nights at once",
  rObservatorySub: "Обсерватория",
  rPost: "Post Office",
  rPostSub: "Почта · night desk",
  rRadio: "Radio 2500",
  rRadioSub: "Радио 2500 · a transmitter between two windows",
  rArcade: "Arcade",
  rArcadeSub: "Аркада · neon basement",
  rAtelier: "Crincident",
  rAtelierSub: "ателье · making things awkward since 2026",
  rKiosk: "Kiosk",
  rKioskSub: "Киоск · city paper, project of the week",
  rTower: "Clock Tower",
  rTowerSub: "Часовая башня · time capsules",
  rStation: "Station",
  rMuseum: "Город N",
  mDayGoneHint: "You can find this day in the Archive.",

  tripAlbum: "Days on the road",
  tripAlbumLead: "Every day is open to both of you — add as many photos as you like.",
  tripAlbumNoDays: "No flights yet — the days appear once the board is set.",
  tripAddPhoto: "photo",
  tripChoosePhoto: "choose a photo",
  tripCaption: "caption",
  tripCaptionPh: "what is happening here…",
  tripTagPlace: "tag the place",
  tripOnMap: "on the map",
  tripOpenPhoto: "open photo",
  tripRemovePhoto: "remove photo",
  tripPhotoCount: (n: number) => `${n} photo${n === 1 ? "" : "s"}`,
  tripPickDay: "day of the trip",
  tripToday: "today",
  tripOpenDay: "open day",
  tripPreviousDay: "previous day",
  tripNextDay: "next day",
  tripDayOf: (n: number, total: number) => `day ${n} of ${total}`,
  tripDocs: "Documents",
  tripDocsLead: "Passport, tickets, bookings — kept apart from the daily album.",
  tripAddDoc: "document",
  tripChooseDoc: "choose a file",
  tripDocCaptionPh: "e.g. booking reference…",
  tripRemoveDoc: "remove document",
  tripDocKind: {
    passport: "passport", ticket: "ticket", hotel: "booking", other: "other",
  } as Record<"passport" | "ticket" | "hotel" | "other", string>,
  tripExport: "export",
  tripExportTitle: "The road · summary",
  tripExportLead: "The whole trip: where you were, what happened, photos with place and time.",
  tripExportBack: "back to days",
  tripExportPrint: "print / PDF",
  tripExportNoPhotos: "no photos",
  tripMapLabel: "map of the road",
  tripMapOpen: "open the map",
  tripSlot: { "утро": "morning", "день": "afternoon", "вечер": "evening" } as Record<string, string>,
  tripVoice: "record a voice note",
  tripVoiceStop: "stop",
  tripRemoveVoice: "remove recording",
  tripVoiceNoMic: "the microphone is unavailable. check the permission and try again.",
  tripDossierDays: "days on the road",
  tripDossierPhotos: "photographs",
  tripDossierPlaced: "with a place",
  tripDossierTogether: "documented days together",
  tripDossierSpent: "spent",
  tripLandingOn: "on into the city",
  tripLanded: (plek: string) => `he has landed · ${plek}`,
  tripLandedKm: (km: number) => `${km} km between you`,
  tripLandedIn: (n: number) => n <= 1 ? "any moment now" : `in ${n} minute${n === 1 ? "" : "s"}`,
  tripQueued: (n: number) => `${n} file${n === 1 ? "" : "s"} waiting to upload`,
  sSpendCat: {
    food: "food", transport: "transport", stay: "stay", other: "other",
  } as Record<"food" | "transport" | "stay" | "other", string>,
  sSpendRemove: "remove expense",

  oConstellation: "constellation of the day",
  oBlind: "Answer blind — it only opens together…",
  oSealsAt: "seals at 01:00 MSK",
  errDaySealed: "The day is already sealed — this can no longer be sent.",
  errAnswerFinal: "Your answer is already recorded. It does not change — that is how this day works.",
  errDayRevealed: "The day is already open together. It cannot be rewritten.",
  errStale: "Something changed. Refresh and try again.",
  oConfirmAsk: "Send your answer? It cannot be changed afterwards.",
  oConfirmYes: "yes, send it",
  oConfirmNo: "let me think",
  oDraftKept: "draft kept on this device",
  oUndo: "undo",
  oClear: "erase",
  errRecordEmpty: "The recording came out empty. Try once more.",
  errMic: "Could not start the microphone. Check its permission.",
  errTooLarge: "That file is too large — it did not fit.",
  errFileType: "That kind of file does not fit here.",
  errNotYours: "That one is not yours to open.",
  errGone: "That is no longer there.",
  errAlready: "That is already recorded — it cannot happen twice.",
  errInvalid: "That could not be sent. Check what is filled in.",
  errTooFast: "Too often. Give it a moment.",
  errWaitOther: "Your turn is saved. Waiting for the second.",
  errRoundOver: "That round has already ended.",
  oQuestion: "question",
  oWord: "word",
  oCityWord: "word for the sign",
  oLeaveTrace: "Leave a trace of today",
  oTraceOptional: "optional · seen together after reveal",
  oWordPurpose: "This word becomes the neon sign on today's building.",
  oPhotosRevealed: "photos of the day",
  oYourPhoto: "your photo of the day",
  oPartnerPhoto: (name: string) => `${name}'s photo of the day`,
  oReactPhoto: "leave a sticker on the photo",
  oReactionFrom: (name: string) => `sticker from ${name}`,
  oReactionFinal: "Sticker placed. Your choice is final.",
  echoTitle: "Echo after reveal",
  echoExplain: "Leave one quiet sign — no words needed.",
  echoLeave: "leave an echo",
  echoSkip: "skip for now",
  echoSent: "Your echo stays with this day.",
  echoPending: "Your echo is waiting for a connection and will sync later.",
  echoRetry: "try again",
  echoFrom: (name: string) => `${name} left an echo`,
  oLine: "line",
  oPhoto: "photo",
  oSmell: "smell",
  oSound: "sound",
  oSong: "song",
  oDrawing: "drawing",
  oHis: "his",
  oHers: "hers",
  oOptional: "optional",
  oLinePh: "one line…",
  oWordPh: "a word…",
  oOneFrame: "+ one frame",
  oPhotoOnce: "you can swap the frame until the day opens",
  oPhotoPromptTitle: "Photo of today",
  oPhotoPromptSub: "One real moment. You see each other's photos after the reveal.",
  oChoosePhoto: "Take or choose a photo",
  oPhotoUploading: "Uploading…",
  oPhotoAdded: "Your frame is waiting behind the reveal.",
  oReplacePhoto: "swap the frame",
  oRecord: "● record",
  oStopSend: "■ stop and send",
  oTodayLabel: (what: string) => `${what} · today`,
  oWaiting: (who: string) => `Your star is lit. Waiting for ${who} — it opens together.`,
  oBlindNote1: "She sees your stars, but not your words.",
  oBlindNote2: "Answers stay sealed until you both finish. Everything after the question is optional.",

  mAnswer: "answer",
  mSealed: " · sealed",

  pSend: "Send",
  pDelivered: "delivered",
  pRead: "read ♥",
  pLetter: "letter",
  pCard: "card",
  pCardForYou: "a card for you",
  pSentOn: (d: string) => `sent ${d}`,
  pYourCard: "your card",
  pShelfHis: "his shelf",
  pShelfHers: "her shelf",
  pShelf: (who: string) => `${who} · shelf`,
  pDearN: "Dear N., …",
  pDearR: "Dear R., …",
  pFrom: (who: string) => `from ${who}`,

  gWhereAmI: "Where am I? 2.0",
  gMemoryMetro: "Memory Metro",
  gBasement: "neon basement",
  kSeasonKicker: (s: string) => `season · ${s}`,
  gSloganPh: "slogan…",
  gHangman: "Hangman",
  gSecretWordPh: "secret word…",
  gSealWord: "seal the word",
  gOwnWordWaiting: "Your word is already waiting for the other player.",
  gHangWaiting: "The other player is guessing your word. Every attempt will appear here.",
  gHangmanSolved: "word solved",
  gHangmanLost: "no attempts left",
  gTodayRound: "today",
  gOpenRounds: (n: number) => `in play · ${n}`,
  gYourTurn: "your turn",
  gHowItWorks: "how to play",
  gSameStreetGuide: "Choose your own answer, then predict the other person's choice. A correct prediction earns a Mind Reader point; matching choices leave a permanent spark in the city.",
  gRecommended: "today's cabinet",
  gHangGuide: "You hide a word; the other guesses letter by letter — or risks the whole word. Every miss puts out a lantern; on the seventh the street falls asleep. Guessed — points for both: the fewer lanterns out, the more.",
  gCityGameEffect: "Every finished game sends light along the city axis. A Same Street match remains in the city as a spark.",
  gChooseCabinet: "choose a cabinet",
  gContinue: "continue turn",
  gBackToGames: "← all games",
  gDailyTag: "once a day",
  gStartTag: "start a new one",
  kMine: (n: number) => n > 1 ? `your move · ${n}` : "your move",
  kWaitingFor: (h: string, n = 0): string =>
    `${h === "N" ? "her move" : "his move"}${n > 1 ? ` · ${n}` : ""}`,
  kStreetNew: "not played yet",
  kStreetDone: "played today",
  kSerieTag: (n: number) => `streak · ${n}`,
  kCabError: "the basement isn't answering",
  kRetry: "try again",
  kNewParty: "new round",
  kStarterWord: "a new word",
  rrTitle: "Route Runner",
  rrKicker: "night road",
  rrLead: "six signals to your arrival",
  rrMapLabel: (n: number, total: number) => `route: ${n} of ${total} checkpoints`,
  rrStart: "start",
  rrArrival: "arrival",
  rrYourTurn: "your turn — choose the next signal",
  rrWaiting: "the route is waiting for Nastya",
  rrSteady: "keep the line",
  rrSteadyNote: "one checkpoint forward",
  rrScenic: "follow the light",
  rrScenicNote: "one checkpoint and a shared line",
  rrShortcut: "take the shortcut",
  rrShortcutNote: "two checkpoints, with a detour",
  rrHistory: "recent route signals",
  rrArrived: "you arrived together",
  rrLate: "late, but together",
  rrFinishedLead: "The route stays in the city as a warm trace.",
  rrNew: "a new route",
  rrStarterLead: "Take turns choosing signals until you reach the last star.",
  rrGuide: "This is one shared road with six checkpoints. Take turns: keep the line, follow the light, or take a shortcut. Every ending is your shared trace, never a competition.",
  sWon: (): string => "you won",
  sWonBy: (h: string): string => `${h === "R" ? "Rick" : "Настя"} won`,
  sTheyPromised: (h: string): string => `${h === "R" ? "Rick" : "Настя"} promised`,
  sYouPromised: (): string => "you promised",
  sNoWinner: "no winner — the promises stay unclaimed",
  sOwed: "you owe it",
  sUnused: "not needed",
  kSerieUp: (a: number, b: number) => `our streak: ${a} → ${b}`,
  kSerieBroke: (a: number, b: number) =>
    b ? `the streak broke at ${a} · now ${b}` : `the streak broke at ${a}`,

  cSeal: "seal it",
  cOnDate: "on a date",
  cWhenTogether: "when together",
  cWhenTogetherLong: "when you're both online",
  cWhenSad: "when it's a sad day",
  cNewChapter: "a new chapter",
  cSpecialMoment: "a special moment",
  cStillClosed: "still closed",
  cCanOpen: "can be opened",
  cYoursWaiting: "yours · waiting",
  cDate: "date",
  cLetterToFuturePh: "a letter to the future…",
  cAddPhoto: "+ add a photo",
  cRemovePhoto: "remove photo",
  cPhotoPreview: "photo for the capsule",
  cPhotoAlt: "photo from the capsule",
  cPhotoSealed: "photo sealed inside",
  cSavedOffline: "capsule saved and will be sent when you reconnect",

  sDay: "day",
  sPunchR: "R's punch",
  sPunchN: "N's punch",
  sNewTicketPh: "a new ticket…",
  sAtGate: "at the gate",
  sPlanToday: "plan for today",

  rUntitled: "untitled",
  rWhyPh: "why this one…",
  rLinkPh: "link (spotify / yandex)…",
  rTrackPh: "track…",
  rStop: "■ stop",
  rVoiceIntro: "● voice intro",
  rHasIntro: "✓ intro",
  rWithBridge: " · with a sound bridge",
  rChooseSource: "radio source",
  rCityRadio: "City Radio",
  rSpotifyFeed: "Spotify Feed",
  rSharedPlaylist: "What you listened to",
  rSpotifyLive: "from Spotify · each of you your own half",
  rSpotifyOffline: "The playlist lives at Spotify. Without a connection there is nothing to show — the city radio below still works offline.",
  rSpNotLinkedWho: (who: string) => `${who} has not connected Spotify yet.`,
  rSpNoPlays: "Quiet so far. What you listen to will appear here.",
  rSpNotLinkedShort: "Spotify not connected",
  rSpPaused: "sync paused",
  rSpNeverSyncedShort: "not synced yet",
  rSpStaleShort: (when: string) => `Spotify unreachable · from ${when}`,
  rSpFailedShort: "sync failed",
  rSpLastPlayed: (when: string) => `last track ${when}`,
  rSpQuiet: "quiet so far",
  rSpQuietDay: "Quiet so far today. What you listen to will appear here.",
  rSpSharedHours: (n: number) =>
    `you overlapped in ${n} ${n === 1 ? "hour" : "hours"}`,
  rSpNoSharedHours: "no hour today where you both listened",
  rSearching: "searching Spotify…",
  rNoHits: "Nothing found. Just type it — that works too.",
  rPickTrack: "pick a track",
  rPickHint: "Start typing a title — you can pick it from Spotify.",
  rSpOneTick: "each stroke is one track",
  rSpGap: (from: string, to: string) => `${from} — ${to} · quiet`,
  rSpGapOne: (at: string) => `${at} · quiet`,
  rSpOpen: "open in Spotify",
  rSpotifyExplain: "The name beside a track is not a guess: every line comes out of that person's own account. The «Наши» playlist opens below, straight from Spotify.",
  rOpenSpotify: "open in Spotify",

  kYourVote: " · your vote",
  kBuilt: " — built: ",
  kChooseTogether: "The city offers three projects. Choose one — together.",
  kVoteIn: "Your vote is in — waiting for the second.",
  kYourPartPh: "your part of the project…",

  aTee: "t-shirt",
  aHoodie: "hoodie",
  aCap: "cap",
  aTote: "tote",
  aJustBecause: "general fatigue",
  aContextPh: "department (general fatigue, escalations…)",

  gOnAirWaiting: "You're on air — waiting for the second wave.",
  gSloganComing: "The slogan is on its way…",
  gToAir: "send it out",
  gFourDates: "4 shared dates — in the right order",
  gLetsGo: "let's go",
  gSeasonClean: "The season is still clean.",
  aOtherWrites: "the other half writes the slogan without seeing the item",
  aToMachine: "into the machine",
  aShowcase: "Showcase · mockups",
  kProjectOfWeek: "Project of the week",
  kBuiltTogether: "built together",
  kYourPartReady: "Your part is ready — waiting for the other half.",
  kSubmit: "submit",
  kLoading: "opening the kiosk",
  kLoadingExplain: "Checking projects and issues — nothing appears before the city answers.",
  kErrorLabel: "the kiosk is unavailable",
  kErrorExplain: "Nothing here has changed. Try loading again when the connection returns.",
  kFirstRunLabel: "first visit",
  kFirstRunTitle: "The next story starts here.",
  kFirstRunExplain: "The Kiosk keeps your shared projects and gazette issues. New work appears here when the city prepares it — no empty cards or made-up data.",
  kFirstRunAction: "open Today",
  kMondayBrings: "Monday brings new projects.",
  kGazette: "Gazette",
  kFirstIssue: "The first issue comes out on Sunday — by itself.",
  kHowItWorks: "how the kiosk works",
  kFlowVote: "1 · private vote",
  kFlowContribute: "2 · one part each",
  kFlowBuild: "3 · a new city place",
  kVoteExplain: "You each choose one project privately. Once both votes are in, a match wins; if you differ, the city chooses between your two projects.",
  kVoteMarked: "Your choice is sealed. You can change it while the other person votes; their vote stays hidden.",
  kContributeExplain: "After the choice, you each add one part: text, photo or sound. The other contribution stays hidden until completion.",
  kBuildExplain: "When both parts are ready, the project becomes a permanent building in the Archive Quarter and appears in the Gazette.",
  kCurrentStep: "now",
  kLatestIssue: "latest issue",
  kOlderIssues: (n: number) => `older issues · ${n}`,
  mDictOfDay: "Dictionary of this day",
  oOneStroke: "in one stroke or a hundred",
  oDone: "The constellation is complete.",
  oSoundDone: "The sound of the day is recorded.",
  oDrawingDone: "The drawing of the day is ready.",
  oSendIt: "send",
  pArrived: "Arrived",
  pEmptyYet: "nothing yet",
  pTravelling: "On the way",
  pWrite: "Write",
  pInstant: "right away",
  pQuick: "quick · ±30 sec",
  pMedium: "today · 6–18h",
  pSlow: "slow · 2–5 days",
  pRandom: "city decides",
  pWindowFrom: "not before",
  pWindowTo: "not after",
  pRandomNote: "The city secretly picks a moment inside this window.",
  pCityDecides: "the city will choose the moment",
  pSomewhereBetween: (a: string, b: string) => `sometime between ${a} and ${b}`,
  pCardsTravel: "Cards travel across the map — you see the envelope, not the text.",
  pSent: "Sent",
  rBothCities: "2500 kHz · both cities on air",
  rBroadcasts: "Broadcasts",
  rCassettes: "Cassettes",
  rListen: "▶ listen",
  rEmpty: "The air is empty. The first track is yours.",
  rAddToAir: "Add to the air",
  sBoard: "Departures",
  sEasyDay: "An easy day. Only what's green — the rest can wait.",
  sTickets: "Tickets · two punches = green light",
  sPlans: "Our plans",
  sPlansExplain: "One person proposes a plan. Each adds their own punch; two punches put it on the Today Pass.",
  sNoPlans: "No plans yet. Add the first one and the other person can confirm it.",
  sTimeOfDay: "time of day",
  sMorning: "morning",
  sAfternoon: "afternoon",
  sEvening: "evening",
  sMaintenance: "maintenance and old import",
  sLuggage: "Luggage · budget · phrases",
  sSuitcase: "suitcase",
  sLeft: "left",
  sPhrases: "phrases",
  sCalendar: "calendar · .ics",
  sCalendarSaved: "calendar saved",
  sImportOld: "import from the old planner",
  sPasteJson: "paste the JSON export (two devices — one at a time)",
  sDupIgnored: "duplicates are ignored · append-only",
  sImport: "import",
  cEmpty: "The tower is empty — seal the first capsule.",
  cOpened: "Opened",
  cSealCapsule: "seal the capsule",
  cSheSeesLock: "she'll see the lock, not the text",
  sChapterTwo: "CHAPTER II · ······",
  sNotYet: "NOT YET",
  sLessons: "Rick's lessons",
  sLessonsLead: "When he is busy. Moscow time first, his time beside it.",
  sLessonsNone: "No lessons in the next two weeks.",
  sLessonNow: (tot: string) => `In a lesson now — until ${tot} Moscow time.`,
  sLessonsStale: (when: string) => `schedule from ${when}`,
  sLessonsFailed: "could not refresh the schedule",
  sMsk: "MSK",
  sHisTime: "his time",
  sTrip: "The meeting date",
  sTripExplain: "Put the date on the board and the city starts counting down. Three days before it the quarter turns to “approaching” and the Gate opens.",
  sTripNone: "No date on the board yet.",
  sTripMeet: "we meet",
  sTripEnd: "we part",
  sTripSave: "put it on the board",
  sTripClear: "clear the date",
  sTripBad: "The end has to come after the beginning.",
  sGate: "The Gate",
  sGateExplain: "Two keys, turned within ninety seconds of each other. That is the only way the chapter becomes “together” — neither of you can decide it alone.",
  sGateTurn: "turn your key",
  sGateWaiting: "Your key is turned. Waiting for the second one — within 90 seconds.",
  sGateTogether: "You are together. The city knows.",
  pArrivesAt: (eta: string) => `arrives ~${eta}`,
  pOnWayEta: (eta: string) => `on the way · ~${eta}`,
  mNeon: (text: string) => `neon: «${text}»`,
  mCityMemory: (kind: string) => ({ lantern: "you left a light on here", postcard: "this day left a postcard",
    radio: "a voice still carries from the roof", wildflower: "the day left a little garden" } as Record<string, string>)[kind] ?? "the day left a small detail",
  mCityBuiltBy: (names: string[]) => `built together by: ${names.join(" · ")}`,
  cityBuildPending: "the city is finishing the building · check the connection",
  cityBuildRetry: "check again",
  cityBuildFresh: "the city saw it · the building is now part of the city",
  mPartnerLeft: (who: string, n: number) => `${who} left ${n} signal(s) here — they open together.`,
  mMailThatDay: (n: number) => `Mail arrived that day: ${n}.`,
  kLandmark: (title: string) => `«${title}» — now a landmark in the city.`,
  kChosen: (title: string) => `chosen · ${title}`,
  kNewWords: (w: string) => `new words: ${w}`,
  kOnAirNow: (title: string) => ` — on air: ${title}`,
  kPostStats: (delivered: number, travelling: number) => `post: ${delivered} delivered · ${travelling} travelling`,
  kProjectLabel: (title: string) => ` — project: ${title}`,
  rWeekAir: (week: string) => `${week} · on air`,
  rTrackCount: (n: number) => `${n} ${n === 1 ? "track" : "tracks"}`,
  gWhatOnIt: (g: string) => `${g} — and what goes on it? Write it blind.`,
  gCabinet: (name: string) => `Cabinet of the day · ${name}`,
  aColor: (c: string) => `colour ${c}`,
  gFromRealDays: "from real, opened days",
  gSketchRelay: "Blind Sketch Relay",
  gSameStreet: "Same Street",
  ssKicker: "daily game · under a minute",
  ssChooseMine: "What would you choose?",
  ssChooseTheirs: "What will the other choose?",
  ssSeal: "seal both answers",
  ssSealed: "Your answers are sealed",
  ssWaiting: "Waiting for the second window — answers stay hidden.",
  ssTheirChoice: "choice",
  ssTheirPrediction: "prediction",
  ssMindReader: "mind reader +1",
  ssMissed: "missed it",
  ssSpark: "Same choice — a shared spark lit up in the city\u00a0✦",
  ssDifferent: "Two different streets. A new question arrives tomorrow.",
  ssScore: "Mind Readers",
  ssTodayDone: "played today",
  ssOnline: "You need a connection to seal this round.",
  cDaysBuilt: (n: number) => `${n} houses`,
  cUntil: (when: string) => `until ${when}`,
  cCapsuleAria: (cond: string) => `capsule · ${cond}`,
  sWeAreTired: "we're tired — make the day lighter",
  sUndoTired: "we have energy again — restore plans",
  sTodayPass: "Today Pass",
  sRemovePlan: (title: string) => `remove plan: ${title}`,
  dkTogether: "together",
  dkConstruction: "under construction",
  dkDark: "quiet",
  dkPark: "park",
  dkAlley: "alley",
  toastWindowLit: (from: string) => `✦ ${from}'s Огонёк just lit your window.`,
  toastOgonekSent: (to: string) => `✦ Огонёк sent to ${to}: their window lights up and they may receive a quiet notification.`,
  toastOgonekFailed: "Огонёк did not arrive — check the connection and try again.",

  gCringeMachine: "Crincident Machine",
  aShowcaseEmpty: "The showcase is waiting for its first piece.",

  aMachine: "The machine · pick an item, the slogan arrives blind",
  aStepOne: "step 1 of 2 · build the item",
  aStepTwo: "step 2 of 2 · blind slogan",
  aHiddenDetails: "The colour and secret context appear only after the slogan.",
  aWaitingSlogan: "Item sent. Waiting for a slogan from the other window.",
  aReveal: "reveal the item",
};

export const DICTS: Record<Locale, Dict> = { ru, en };

/* Maandnamen op volgorde, zodat een datum met één index vertaald is. */
export const MONTH_KEYS = ["mJan", "mFeb", "mMar", "mApr", "mMay", "mJun",
  "mJul", "mAug", "mSep", "mOct", "mNov", "mDec"] as const;
